/**
 * Lead Status values matching backend AppStatus enum
 * These represent the different states a lead can be in
 *
 * IMPORTANT: These values MUST match the backend enum in:
 * fex-platform-service/src/constants/lead.constants.ts
 */
export enum LeadStatus {
  UWR = 'Underwriting',
  NEW_LEAD = 'New lead',
  APPOINTMENT = 'Appt',
  NO_SALE = 'No sale',
  APPROVED = 'Sold & Approved',
  BAD_NUMBER = 'Bad number',
  DO_NOT_CALL = 'Do not call',
  DECLINED = 'Declined',
  LAPSED = 'Lapsed',
  DISPOSED = 'Disposed',
}

/**
 * Display names for lead status values
 * Used for UI display and user-friendly messages
 */
export const LEAD_STATUS_DISPLAY_NAMES: Record<LeadStatus, string> = {
  [LeadStatus.UWR]: 'Underwriting',
  [LeadStatus.NEW_LEAD]: 'Leads',
  [LeadStatus.APPOINTMENT]: 'Appt',
  [LeadStatus.NO_SALE]: 'No sale',
  [LeadStatus.APPROVED]: 'Sold & Approved',
  [LeadStatus.BAD_NUMBER]: 'Bad number',
  [LeadStatus.DO_NOT_CALL]: 'Do not call',
  [LeadStatus.DECLINED]: 'Declined',
  [LeadStatus.LAPSED]: 'Lapsed',
  [LeadStatus.DISPOSED]: 'Disposed',
};

/**
 * Legacy lead status values for backward compatibility
 * Maps old database values to new enum values
 */
export const LEGACY_LEAD_STATUS_MAP: Record<string, LeadStatus> = {
  uwr: LeadStatus.UWR,
  newl: LeadStatus.NEW_LEAD,
  new: LeadStatus.NEW_LEAD,
  appt: LeadStatus.APPOINTMENT,
  no: LeadStatus.NO_SALE,
  approved: LeadStatus.APPROVED,
  bad: LeadStatus.BAD_NUMBER,
  dnc: LeadStatus.DO_NOT_CALL,
  declined: LeadStatus.DECLINED,
  lapsed: LeadStatus.LAPSED,
  disposed: LeadStatus.DISPOSED,
};

/**
 * Type for lead status values
 */
export type LeadStatusValue = LeadStatus;

/**
 * Get all valid lead status values
 */
export function getAllLeadStatusValues(): LeadStatusValue[] {
  return Object.values(LeadStatus);
}

/**
 * Get display name for a lead status
 */
export function getLeadStatusDisplayName(status: LeadStatusValue): string {
  return LEAD_STATUS_DISPLAY_NAMES[status] || status;
}

/**
 * Get display name for any status string (handles raw values like "New lead")
 * Use this when displaying status in the UI
 */
export function getStatusDisplayName(status: string | undefined | null): string {
  if (!status) return '';
  // Map "New lead" to "Leads" for display
  if (status === 'New lead' || status === 'new lead') {
    return 'Leads';
  }
  // Try enum lookup
  if (Object.values(LeadStatus).includes(status as LeadStatus)) {
    return LEAD_STATUS_DISPLAY_NAMES[status as LeadStatus] || status;
  }
  return status;
}

/**
 * Check if a string is a valid lead status
 */
export function isValidLeadStatus(status: string): status is LeadStatusValue {
  return (
    Object.values(LeadStatus).includes(status as LeadStatusValue) ||
    Object.keys(LEGACY_LEAD_STATUS_MAP).includes(status.toLowerCase())
  );
}

/**
 * Normalize a lead status string to a valid LeadStatus enum value
 */
export function normalizeLeadStatus(status: string): LeadStatus {
  // Try direct match first
  if (Object.values(LeadStatus).includes(status as LeadStatus)) {
    return status as LeadStatus;
  }

  // Try legacy map
  const normalized = LEGACY_LEAD_STATUS_MAP[status.toLowerCase()];
  if (normalized) {
    return normalized;
  }

  // Default to NEW_LEAD if unknown
  return LeadStatus.NEW_LEAD;
}

/**
 * Get all legacy lead status values
 */
export function getAllLegacyLeadStatusValues(): string[] {
  return Object.keys(LEGACY_LEAD_STATUS_MAP);
}
